import { useState, useRef, useEffect } from 'react';
import { Icon, Spinner } from './ui/Icon.js';
import { Button } from './ui/Button.js';
import type { CollectionPreview } from '../types/index.js';

interface CollectButtonProps {
  preview: CollectionPreview | null;
  onCollect: () => Promise<void>;
  collecting: boolean;
}

export function CollectButton({ preview, onCollect, collecting }: CollectButtonProps) {
  const [showConfirm, setShowConfirm] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    if (!showConfirm) return;
    function handleClick(e: MouseEvent) {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setShowConfirm(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showConfirm]);

  const handleConfirm = async () => {
    setShowConfirm(false);
    await onCollect();
  };

  return (
    <div className="relative" ref={popoverRef} data-testid="collect-button-wrapper">
      <Button variant="primary" onClick={() => setShowConfirm(true)} disabled={collecting || !preview} data-testid="collect-all-btn">
        {collecting ? <Spinner /> : <Icon name="bx-play" />}
        Collect fresh data
      </Button>

      {showConfirm && preview && (
        <div role="dialog" aria-label="Confirm collection" data-testid="collect-confirm" className="absolute right-0 top-full z-50 mt-2 w-72 rounded-lg border border-slate-200 bg-white p-4">
          <p className="text-sm text-slate-700">
            This will run <span className="font-semibold">{preview.total_queries} queries</span> costing
            ~<span className="font-semibold">${preview.estimated_cost.toFixed(2)}</span>.
          </p>
          {preview.region_capture_active && (() => {
            // Region dimension — explain the per-region split so the accurate
            // total_queries doesn't look like it silently doubled.
            const split = Object.entries(preview.per_region_queries ?? {}).sort(([a], [b]) =>
              a.localeCompare(b),
            );
            if (split.length === 0) return null;
            return (
              <p className="mt-1 text-xs text-slate-500" data-testid="collect-confirm-regions">
                Across {split.length} region{split.length !== 1 ? 's' : ''}
                {split.map(([code, count]) => (
                  <span key={code}>
                    <span className="text-slate-300"> · </span>
                    <span className="font-medium text-slate-600">{code}</span> {count}
                  </span>
                ))}
              </p>
            );
          })()}
          <div className="mt-3 flex gap-2 justify-end">
            <Button variant="ghost" onClick={() => setShowConfirm(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleConfirm} data-testid="collect-confirm-btn">
              Proceed
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
